<template>
    <div class="kgr-grid kgr-grid--gutters kgr-grid--bigGutters">
        <div class="kgr-grid__cell">
            <kgr-form-row
                :label="$t('offering.title')"
                :error="error"
                field-name="title"
                required
            >
                <input
                    v-model.trim="offering.title"
                    type="text"
                    class="kgr-input"
                    @keyup.enter="onKeyUpEnter"
                />
            </kgr-form-row>
            <kgr-form-row
                :label="$t('offering.customId')"
                :error="error"
                field-name="offering.customId"
            >
                <input
                    v-model.trim="offering.customId"
                    type="text"
                    class="kgr-input"
                    @keyup.enter="onKeyUpEnter"
                />
            </kgr-form-row>
            <kgr-form-row
                :label="$t('offering.clubExams')"
                :error="error"
                field-name="clubExams"
                required
            >
                <kgr-club-exam-multi-select
                    v-model="offering.clubExams"
                    :club-id="clubId"
                    strict-examinee-type
                />
            </kgr-form-row>
            <kgr-form-row
                :error="error"
                field-name="offering.selectableExams"
            >
                <kgr-checkbox
                    v-model="selectableExams"
                    :label="$t('offering.selectableExams')"
                    inline
                    @change="changeSelectableExams"
                />
            </kgr-form-row>
            <kgr-form-row
                :label="$t('offering.description')"
                :error="error"
                field-name="description"
            >
                <textarea
                    v-model.trim="offering.description"
                    type="text"
                    class="kgr-input"
                ></textarea>
            </kgr-form-row>

            <kgr-form-row
                :label="$t('offering.sports')"
                :error="error"
                field-name="sport"
            >
                <kgr-sports-select
                    v-model="offering.sport"
                    :club-id="clubId"
                    :hide-label="true"
                    display="name"
                />
            </kgr-form-row>

            <kgr-form-row
                :label="$t('offering.requirements')"
                :error="error"
                field-name="requirements"
            >
                <input
                    v-model.trim="offering.requirements"
                    type="text"
                    class="kgr-input"
                    @keyup.enter="onKeyUpEnter"
                />
            </kgr-form-row>
            <div class="kgr-inputGroup">
                <div class="kgr-grid kgr-grid--gutters kgr-grid--bigGutters">
                    <div class="kgr-grid__cell">
                        <kgr-form-row
                            :label="$t('offering.minParticipants')"
                            :error="error"
                            field-name="minParticipants"
                        >
                            <input
                                v-model.number="offering.minParticipants"
                                type="number"
                                min="0"
                                class="kgr-input"
                                @input="checkParticipants"
                                @keyup.enter="onKeyUpEnter"
                            />
                        </kgr-form-row>
                    </div>
                    <div class="kgr-grid__cell">
                        <kgr-form-row
                            :label="$t('offering.maxParticipants')"
                            :error="error"
                            field-name="maxParticipants"
                        >
                            <input
                                v-model.number="offering.maxParticipants"
                                type="number"
                                min="0"
                                class="kgr-input"
                                @input="checkParticipants"
                                @keyup.enter="onKeyUpEnter"
                            />
                        </kgr-form-row>
                    </div>
                </div>
            </div>
            <hr />
            <kgr-comment-field-setting-component
                :offering="offering"
                :error="error"
            />
            <hr />
            <div class="kgr-row">
                {{ $t('offering.locationInfo') }}
            </div>
            <kgr-form-row
                :label="$t('offering.locations')"
                :error="error"
                field-name="selectedLocation"
                required
            >
                <kgr-searchable-dropdown
                    v-model="selectedLocation"
                    :options="locations"
                    display="name"
                    @post-input="getLocationAreas"
                />
            </kgr-form-row>
            <kgr-form-row
                :label="$t('offering.areas')"
                :error="error"
                field-name="selectedArea"
                required
            >
                <kgr-searchable-dropdown
                    v-model="selectedArea"
                    :options="areas"
                    display="name"
                    @post-input="getAreaFields"
                />
            </kgr-form-row>
            <kgr-form-row
                :label="$t('offering.fields')"
                :error="error"
                field-name="selectedFields"
                required
            >
                <kgr-multi-select
                    v-model="selectedFields"
                    :options="filteredFieldlist"
                    :inline="false"
                    searchable
                    @post-input="updateFields"
                />
            </kgr-form-row>
            <hr />
            <kgr-form-row
                :label="$t('offering.instructors')"
                :error="error"
                field-name="selectedInstructors"
            >
                <kgr-multi-select
                    v-model="selectedInstructors"
                    :options="filteredInstructors"
                    :inline="false"
                    searchable
                    @post-input="updateInstructors"
                />
            </kgr-form-row>
            <br />
        </div>
        <div class="kgr-grid__cell">
            <kgr-accordion>
                <kgr-accordion-item
                    :title="$t('offering.details')"
                    overflow
                >
                    <kgr-offering-booking-requirements-component
                        :offering="offering"
                        :club-id="clubId"
                        :error="error"
                    />
                </kgr-accordion-item>
                <kgr-accordion-item
                    ref="refAddInterval"
                    :title="$t('offering.addInterval')"
                    :open="true"
                >
                    <kgr-offering-create-appointment-series-component
                        v-model="offering.appointmentSeriesList"
                        :offering="offering"
                        :save-action="saveAction"
                        @change="updateAppointmentSeries"
                        @appointmentInvalid="$emit('appointmentInvalid', $event)"
                    />
                    <br />
                    <div class="kgr-row">
                        <h4 class="kgr-headline kgr-headline--h4">
                            {{ $t('offering.appointmentsRangeInfo') }}
                        </h4>
                    </div>
                    <kgr-form-row
                        :error="error"
                        field-name="takesPlaceInSchoolHolidays"
                    >
                        <kgr-checkbox
                            v-model="takesPlaceInSchoolHolidays"
                            :label="$t('offering.takesPlaceInSchoolHolidays')"
                            inline
                            @change="changeInSchoolHolidays"
                        />
                        <kgr-checkbox
                            v-model="takesPlaceOnPublicHoliday"
                            :label="$t('offering.takesPlaceOnPublicHoliday')"
                            inline
                            @change="changeOnPublicHolidays"
                        />
                    </kgr-form-row>
                    <br />
                    <div class="kgr-row">
                        <h4 class="kgr-headline kgr-headline--h4">
                            {{ $t('offering.bookingMode') }}
                        </h4>
                    </div>
                    <kgr-form-row
                        :error="error"
                        field-name="bookingMode"
                    >
                        <kgr-radio
                            v-model="offering.bookingMode"
                            name="bookingMode"
                            :options="bookingModeOptions"
                            :inline="true"
                        />
                    </kgr-form-row>
                </kgr-accordion-item>
                <kgr-accordion-item
                    ref="refAddPrice"
                    :title="$t('offering.addprices')"
                    :open="true"
                >
                    <div class="kgr-row kgr-grid__cell">
                        <kgr-price-table
                            :prices="getFullPriceInformation()"
                            @remove_price="removePrice"
                            @edit_price="editPrice"
                        />
                    </div>
                    <div v-show="showEditPrice">
                        <div class="kgr-row">
                            {{ $t('offering.addPriceInfo1') }}<br />
                            {{ $t('offering.addPriceInfo2') }}
                        </div>
                        <kgr-form-row
                            :label="$t('offering.pricetype')"
                            :error="priceError"
                            field-name="offeringPriceType"
                            required
                        >
                            <kgr-generic-select
                                v-model="offeringPriceType"
                                :options="priceTypes"
                                display="name"
                            />
                        </kgr-form-row>
                        <div v-show="offeringPriceType && offeringPriceType.value === 'department_price'">
                            <kgr-form-row
                                :label="$t('offering.price.departement')"
                                :error="priceError"
                                field-name="offeringPriceDepartment"
                                required
                            >
                                <kgr-searchable-dropdown
                                    v-model="offeringPriceDepartment"
                                    :value="offeringPriceDepartment"
                                    :options="departmentlist"
                                    display="label"
                                />
                            </kgr-form-row>
                        </div>
                        <div v-show="offeringPriceType && offeringPriceType.value === 'team_price'">
                            <kgr-form-row
                                :label="$t('offering.price.team')"
                                :error="priceError"
                                field-name="offeringPriceTeam"
                                required
                            >
                                <kgr-searchable-dropdown
                                    v-model="offeringPriceTeam"
                                    :value="offering.offeringPriceTeam"
                                    :options="teams"
                                    display="label"
                                />
                            </kgr-form-row>
                        </div>
                        <br />
                        <div class="kgr-inputGroup">
                            <kgr-radio
                                v-model="offeringPricing"
                                :options="offeringPricings"
                                :inline="true"
                                @post-input="checkIfFree"
                            />
                        </div>

                        <div
                            v-show="offeringPricing !== 'free'"
                            class="kgr-inputGroup"
                        >
                            <kgr-form-row
                                :label="$t('offering.clubFee')"
                                :error="priceError"
                                field-name="offeringPricesClubFee"
                                required
                            >
                                <kgr-searchable-dropdown
                                    v-model="offeringPricesClubFee"
                                    :options="clubFeeList"
                                    display="name"
                                />
                            </kgr-form-row>

                            <kgr-form-row
                                :label="$t('offering.price')"
                                :error="priceError"
                                field-name="offeringPrice"
                            >
                                <input
                                    v-model.trim="offeringPrice"
                                    class="kgr-input text-right"
                                    @enter="onKeyUpEnter"
                                />
                            </kgr-form-row>
                        </div>
                        <br />
                        <button
                            class="kgr-button"
                            @click="clearEditPrice"
                        >
                            {{ $t('offering.cancel') }}
                        </button>
                        <button
                            v-if="offeringPricesId"
                            class="kgr-button kgr-button--accent"
                            @click="addPrice"
                        >
                            {{ $t('offering.changePrice') }}
                        </button>
                        <button
                            v-else
                            class="kgr-button kgr-button--accent"
                            @click="addPrice"
                        >
                            {{ $t('offering.addprice') }}
                        </button>
                    </div>
                    <br />
                    <button
                        v-show="!showEditPrice"
                        class="kgr-button kgr-button--accent"
                        @click="toggleShowEditPrice"
                    >
                        {{ $t('offering.addprice') }}
                    </button>
                </kgr-accordion-item>
                <kgr-offering-items
                    v-if="salesListModuleEnabled"
                    :club-id="clubId"
                    :offering="offering"
                    :is-editable="canEditSalesListOfferingItems"
                    @selected="$emit('offeringItemsSelected', $event)"
                />
            </kgr-accordion>
            <hr />
            <div class="kgr-row">
                <div class="kgr-row">
                    <h4 class="kgr-headline kgr-headline--h4">
                        {{ $t('offering.bookableStart') }}
                    </h4>
                </div>
                <div class="kgr-inputGroup">
                    <kgr-radio
                        v-model="offeringBookingType"
                        :options="offeringBookingTypes"
                        :inline="true"
                        @post-input="switchOfferingBookingType"
                    />
                </div>

                <div v-show="offeringBookingType === 'perOffering'">
                    <div class="kgr-inputGroup">
                        <div class="kgr-grid kgr-grid--gutters kgr-grid--bigGutters">
                            <div class="kgr-grid__cell">
                                <kgr-form-row
                                    :label="$t('offering.bookingStart')"
                                    :error="error"
                                    field-name="bookingStart"
                                >
                                    <kgr-date-picker
                                        v-model="bookingStart"
                                        @post-input="setBookingStartDate"
                                    />
                                </kgr-form-row>
                            </div>
                            <div class="kgr-grid__cell">
                                <kgr-form-row
                                    :label="$t('offering.bookingEnd')"
                                    :error="error"
                                    field-name="bookingEnd"
                                >
                                    <kgr-date-picker
                                        v-model="bookingEnd"
                                        @post-input="setBookingEndDate"
                                    />
                                </kgr-form-row>
                            </div>
                        </div>
                    </div>
                    <div class="kgr-inputGroup">
                        <div class="kgr-grid kgr-grid--gutters kgr-grid--bigGutters">
                            <div class="kgr-grid__cell">
                                <kgr-form-row
                                    :label="$t('offering.cancellableUntilDate')"
                                    :error="error"
                                    field-name="cancellableUntilDate"
                                >
                                    <kgr-date-picker
                                        v-model="cancellableUntilDate"
                                        @post-input="setCancellableUntilDate"
                                    />
                                </kgr-form-row>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-show="offeringBookingType === 'perAppointment'">
                    <div class="kgr-inputGroup">
                        <div class="kgr-grid kgr-grid--gutters kgr-grid--center">
                            <div class="kgr-grid__cell kgr-inputGroup">
                                <kgr-form-row
                                    :label="$t('offering.bookingStart')"
                                    :error="error"
                                    field-name="offering.bookingStartPerAppointmentAmount"
                                >
                                    <input
                                        v-model.number="offering.bookingStartPerAppointmentAmount"
                                        type="number"
                                        min="1"
                                        class="kgr-input"
                                        :placeholder="$t('offeringBookingType.perAppointment.amount')"
                                    />
                                </kgr-form-row>
                            </div>
                            <div class="kgr-grid__cell">
                                <kgr-form-row
                                    :label="$t('offeringBookingType.perAppointment.label')"
                                    :error="error"
                                    field-name="offeringBookingStartPerAppointmentType"
                                >
                                    <kgr-generic-select
                                        v-model="offeringBookingStartPerAppointmentType"
                                        :options="bookingPerAppointmentTypes"
                                        display="name"
                                        @post-input="setBookingPerAppointmentType"
                                    />
                                </kgr-form-row>
                            </div>
                        </div>
                    </div>
                    <div class="kgr-inputGroup">
                        <div class="kgr-grid kgr-grid--gutters kgr-grid--center">
                            <div class="kgr-grid__cell kgr-inputGroup">
                                <kgr-form-row
                                    :label="$t('offering.bookingEnd')"
                                    :error="error"
                                    field-name="offering.bookingEndPerAppointmentAmount"
                                >
                                    <input
                                        v-model.number="offering.bookingEndPerAppointmentAmount"
                                        type="number"
                                        min="1"
                                        class="kgr-input"
                                        :placeholder="$t('offeringBookingType.perAppointment.amount')"
                                    />
                                </kgr-form-row>
                            </div>
                            <div class="kgr-grid__cell">
                                <kgr-form-row
                                    :label="$t('offeringBookingType.perAppointment.label')"
                                    :error="error"
                                    field-name="offeringBookingEndPerAppointmentType"
                                >
                                    <kgr-generic-select
                                        v-model="offeringBookingEndPerAppointmentType"
                                        :options="bookingPerAppointmentTypes"
                                        display="name"
                                        @post-input="setBookingPerAppointmentType"
                                    />
                                </kgr-form-row>
                            </div>
                        </div>
                    </div>
                    <div class="kgr-inputGroup">
                        <div class="kgr-grid kgr-grid--gutters kgr-grid--center">
                            <div class="kgr-grid__cell kgr-inputGroup">
                                <kgr-form-row
                                    :label="$t('offering.cancellableUntilDate')"
                                    :error="error"
                                    field-name="cancellableUntilAmount"
                                >
                                    <input
                                        v-model.number="offering.cancellableUntilAmount"
                                        type="number"
                                        min="1"
                                        class="kgr-input"
                                        :placeholder="$t('offeringBookingType.perAppointment.amount')"
                                    />
                                </kgr-form-row>
                            </div>
                            <div class="kgr-grid__cell">
                                <kgr-form-row
                                    :label="$t('offeringBookingType.perAppointment.label')"
                                    :error="error"
                                    field-name="cancellableUntilType"
                                >
                                    <kgr-generic-select
                                        v-model="cancellableUntilType"
                                        :options="bookingPerAppointmentTypes"
                                        display="name"
                                        @post-input="setCancellableUntilType"
                                    />
                                </kgr-form-row>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <hr />
            <kgr-form-row
                :label="$t('offering.executiontype')"
                :error="error"
                field-name="executionType"
            >
                <kgr-generic-select
                    v-model="executionType"
                    :options="executionTypes"
                    :hide-label="true"
                    display="name"
                    @post-input="changeExecutionType"
                />
            </kgr-form-row>
            <kgr-form-row
                v-if="urlNeeded"
                :label="$t('offering.onlineUrl')"
                :error="error"
                field-name="onlineUrl"
            >
                <input
                    v-model.trim="offering.onlineUrl"
                    class="kgr-input text-right"
                    @enter="onKeyUpEnter"
                />
            </kgr-form-row>
            <br />
            <kgr-form-row
                :error="error"
                field-name="offering.showAvailablePlaces"
            >
                <kgr-checkbox
                    v-model="showAvailablePlaces"
                    :label="$t('offering.showAvailablePlaces')"
                    inline
                    @change="changeShowAvailablePlaces"
                />
            </kgr-form-row>
            <kgr-form-row
                :error="error"
                field-name="registrationRequested"
            >
                <kgr-checkbox
                    v-model="registrationRequested"
                    :label="$t('offering.login')"
                    inline
                    @change="changeRegistrationRequested"
                />
            </kgr-form-row>
            <hr />
            <div class="kgr-row">
                <h4 class="kgr-headline kgr-headline--h4">
                    {{ $t('offering.publishing') }}
                </h4>
            </div>
            <kgr-form-row
                :error="error"
                field-name="offeringInactive"
            >
                <kgr-checkbox
                    v-model="inactive"
                    :label="$t('offering.inactive')"
                    inline
                    @change="changeStatus"
                />
            </kgr-form-row>
            <kgr-form-row
                v-if="hasTeamsSelected"
                :error="error"
                field-name="accessibleForTeamMembersOnly"
            >
                <kgr-checkbox
                    v-model="accessibleForTeamMembersOnly"
                    :label="$t('offering.accessibleForTeamMembersOnly')"
                    inline
                    @change="changeAccessibleForTeamMembersOnly"
                />
            </kgr-form-row>
        </div>
    </div>
</template>

<script>
import Accordion from '../common/accordion/Accordion.vue';
import AccordionItem from '../common/accordion/AccordionItem.vue';
import Checkbox from '../common/form/checkbox/Checkbox.vue';
import ClubExamMultiSelect from '../common/form/select/ClubExamMultiSelect.vue'
import DatePicker from '../common/form/picker/DatePicker.vue';
import Error from '../../js/model/Error.js';
import FormRow from '../common/form/FormRow.vue';
import GenericRadio from '../common/form/radio/GenericRadio.vue';
import GenericSelect from '../common/form/select/GenericSelect.vue';
import Helper from '../../js/accounting/Helper.js';
import Instructors from '../../js/repository/InstructorRepository.js';
import KgrCommentFieldSettingComponent from './KgrCommentFieldSettingComponent.vue';
import KgrOfferingBookingRequirementsComponent from './KgrOfferingBookingRequirementsComponent.vue';
import KgrOfferingCreateAppointmentSeriesComponent from './KgrOfferingCreateAppointmentSeriesComponent.vue';
import LocationRepository from '../../js/repository/LocationRepository.js';
import MultiSelect from '../common/form/select/MultiSelect.vue';
import Offering from '../../js/dto/offering/Offering.js';
import OfferingClubFeeRepository from '../../js/repository/club/offering/OfferingClubFeeRepository.js';
import OfferingDepartmentRepository from '../../js/repository/club/offering/OfferingDepartmentRepository.js';
import OfferingItems from './OfferingItems.vue';
import OfferingTeamRepository from '../../js/repository/club/offering/OfferingTeamRepository.js';
import PriceFormatter from '../../js/price/PriceFormatter.js';
import PriceTable from './PriceTable.vue';
import SearchableDropdown from '../common/form/select/SearchableDropdown.vue';
import SportsSelect from '../common/form/select/SportsSelect.vue';
import moment from 'moment';

export default {
    components: {
        'kgr-accordion': Accordion,
        'kgr-accordion-item': AccordionItem,
        'kgr-checkbox': Checkbox,
        'kgr-club-exam-multi-select': ClubExamMultiSelect,
        'kgr-comment-field-setting-component': KgrCommentFieldSettingComponent,
        'kgr-date-picker': DatePicker,
        'kgr-form-row': FormRow,
        'kgr-generic-select': GenericSelect,
        'kgr-multi-select': MultiSelect,
        'kgr-offering-booking-requirements-component': KgrOfferingBookingRequirementsComponent,
        'kgr-offering-create-appointment-series-component': KgrOfferingCreateAppointmentSeriesComponent,
        'kgr-offering-items': OfferingItems,
        'kgr-price-table': PriceTable,
        'kgr-radio': GenericRadio,
        'kgr-searchable-dropdown': SearchableDropdown,
        'kgr-sports-select': SportsSelect,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        offeringProp: {
            type: Offering,
            required: true,
        },
        error: {
            type: Error,
            required: true,
        },
        locale: {
            type: String,
            required: false,
            default: 'de-DE',
        },
        currency: {
            type: String,
            required: false,
            default: 'EUR',
        },
        saveAction: {
            type: Boolean,
            required: false,
            default: false,
        },
        salesListModuleEnabled: {
            type: Boolean,
            required: true,
        },
        canEditSalesListOfferingItems: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            offering: null,
            showDetails: false,
            showInterval: false,
            showPrices: false,
            showEditPrice: false,
            prices: [],
            setByStatusValue: false,
            offeringPrice: '',
            inactive: true,
            accessibleForTeamMembersOnly: false,
            offeringPricings: {
                'offering.free': Offering.PRICING_FREE,
                'offering.chargeable': Offering.PRICING_CHARGEBLE,
            },
            bookingModeOptions: {
                'offering.bookingModeOptions.offering': Offering.BOOKING_MODE_OFFERING,
                'offering.bookingModeOptions.appointment': Offering.BOOKING_MODE_APPOINTMENT,
            },
            priceTypes: [
                {name: this.$t('offering.all_price'), value: 'all'},
                {name: this.$t('offering.active_members_price'), value: 'active_members_price'},
                {name: this.$t('offering.passive_members_price'), value: 'passive_members_price'},
                {name: this.$t('offering.guest_members_price'), value: 'guest_members_price'},
                {name: this.$t('offering.department_price'), value: 'department_price'},
                {name: this.$t('offering.team_price'), value: 'team_price'},
            ],
            executionTypes: [
                {name: this.$t('offering.presence'), value: Offering.EXECUTION_TYPE_PRESENCE},
                {name: this.$t('offering.online'), value: Offering.EXECUTION_TYPE_ONLINE},
                {name: this.$t('offering.hybrid'), value: Offering.EXECUTION_TYPE_HYBRID},
            ],
            offeringBookingTypes: {
                'offeringBookingType.perOffering': Offering.BOOKING_TYPE_PER_OFFERING,
                'offeringBookingType.perAppointment': Offering.BOOKING_TYPE_PER_APPOINTMENT,
            },
            bookingPerAppointmentTypes: [
                {name: this.$t('offeringBookingType.perAppointment.minutes'), value: 'minutes'},
                {name: this.$t('offeringBookingType.perAppointment.hours'), value: 'hours'},
                {name: this.$t('offeringBookingType.perAppointment.days'), value: 'days'},
                {name: this.$t('offeringBookingType.perAppointment.weeks'), value: 'weeks'},
                {name: this.$t('offeringBookingType.perAppointment.months'), value: 'months'},
            ],
            offeringPricing: Offering.PRICING_FREE,
            memberClubFees: [],
            offeringFee: null,
            offeringPricesClubFee: null,
            offeringPricesId: null,
            offeringInterval: null,
            offeringIntervalId: null,
            offeringWeekdays: {},
            offeringWeekday: null,
            offeringPriceType: null,
            currentlyEditedOfferingPriceIndex: null,
            offeringStartDate: '',
            offeringIntervalEndDate: '',
            offeringEndDate: '',
            offeringStartTime: '',
            offeringEndTime: '',
            offeringType: [],
            offeringPriceTeam: '',
            offeringPrices: [],
            sports: [],
            clubFeeList: [],
            locations: [],
            locationsFullArray: [],
            areaFullArray: [],
            areas: [],
            fieldList: [],
            teams: [],
            instructors: [],
            departmentlist: [],
            selectedFields: [],
            selectedLocation: '',
            offeringBookingType: null,
            bookingStart: null,
            bookingEnd: null,
            offeringBookingStartPerAppointmentType: null,
            offeringBookingEndPerAppointmentType: null,
            cancellableUntilDate: null,
            cancellableUntilType: null,
            executionType: null,
            showAvailablePlaces: false,
            registrationRequested: false,
            selectableExams: false,
            takesPlaceInSchoolHolidays: false,
            takesPlaceOnPublicHoliday: false,
            selectedArea: '',
            selectedInstructors: [],
            offeringPriceDepartments: [],
            offeringPriceDepartment: '',
            offeringFees: [],
            priceError: new Error(),
        }
    },
    computed: {
        filteredFieldlist() {
            return this.fieldList.filter(field => {
                return this.selectedFields.find(selectedField => selectedField.value === field.value) === undefined
            });
        },
        filteredInstructors() {
            return this.instructors.filter(instructor => {
                return this.selectedInstructors.find(selectedInstructor => selectedInstructor.value === instructor.value) === undefined
            });
        },
        urlNeeded() {
            return this.executionType && this.executionType.value !== 'presence';
        },
        hasTeamsSelected() {
            return Array.isArray(this.offering?.teams) && this.offering.teams.length > 0;
        }
    },
    watch: {
        hasTeamsSelected(hasTeams) {
            if (!hasTeams) {
                this.accessibleForTeamMembersOnly = false;
                this.offering.accessibleForTeamMembersOnly = false;
            }
        }
    },
    created() {
        this.offering = this.offeringProp;
        this.getDepartments().then(result => {
            this.departmentlist.length = 0
            this.departmentlist.push(...result);
        });
        this.getTeams().then(result => {
            this.teams.length = 0
            this.teams.push(...result);
        });
        this.getMemberClubFees().then(result => {
            this.clubFeeList.length = 0
            this.clubFeeList.push(...result);
        });
        this.offering.executionType = this.getExecutionType(this.offering.executionType);
        this.executionType = this.offering.executionType;
        this.selectedInstructors = this.getInstructorsSelect();
        this.getLocations().then(result => {
            this.locations.length = 0
            this.locations.push(...result);
        });
        this.getInstructors().then(result => {
            this.instructors.length = 0
            this.instructors.push(...result);
        });
        this.selectedFields = this.getFieldSelect();
        this.offeringPrices = this.getPrices();
        this.registrationRequested = this.offering.registrationRequested;
        this.showAvailablePlaces = this.offering.showAvailablePlaces;
        this.accessibleForTeamMembersOnly = this.offering.accessibleForTeamMembersOnly;
        this.selectableExams = this.offering.selectableExams;
        this.getBookingdates();
        this.getOfferingStatus();
        this.getBookingType();
        this.getBookingPerAppointmentType();
        this.setBookingPerAppointmentType();
    },
    methods: {
        checkIfFree() {
            if (this.offeringPricing === Offering.PRICING_FREE) {
                this.offeringPrice = '';
                this.offeringPricesClubFee = null;
            }
        },
        formatCurrency(amount) {
            return new Helper().numberFormat(amount, this.locale, this.currency);
        },
        checkParticipants() {
            this.error.clear('maxParticipants');

            const error = this.offering.checkParticipants();
            if (error !== null) {
                this.error.add('maxParticipants', this.$t(error));
            }
        },
        toggleShowEditPrice() {
            this.showEditPrice = !this.showEditPrice;
        },
        clearEditPrice() {
            this.offeringPricesId = null;
            this.offeringPricesClubFee = null;
            this.offeringPriceDepartment = null;
            this.offeringPriceTeam = null;
            this.offeringPriceType = null;
            this.offeringPrice = null;
            this.offeringPricing = Offering.PRICING_FREE;
            this.showEditPrice = false;
            this.currentlyEditedOfferingPriceIndex = null;
        },
        getOfferingStatus() {
            this.inactive = !this.offering.isPublished();
        },
        updateFields() {
            this.offering.fields = [];
            this.selectedFields.forEach(field => this.offering.fields.push({
                'id': field.value,
                'name': field.label
            }));
        },
        updateInstructors() {
            this.offering.instructors = [];
            this.selectedInstructors.forEach(instructor => this.offering.instructors.push({
                'id': instructor.value,
                'name': instructor.label
            }));
        },
        getFullPriceInformation() {
            const result = [];
            if (this.offeringPrices) {
                this.offeringPrices.forEach(
                    price => {
                        result.push({
                            'clubFee': price.clubFee ? this.clubFeeList.find(clubFee => clubFee.value === price.clubFee.id) : null,
                            'department': this.departmentlist.find(department => department.value === price.department),
                            'id': price.id,
                            'team': this.teams.find(team => team.value === price.team),
                            'divergentAmount': price.divergentAmount ? (price.divergentAmount.numerator / price.divergentAmount.denominator) : null,
                            'type': this.priceTypes.find(priceTmp => priceTmp.value === price.type)
                        });
                    }
                );
            }
            this.offering.prices = this.offeringPrices;
            return result;
        },
        addPrice() {
            this.priceError = new Error();
            this.priceError.details = [];

            if (!this.offeringPriceType) {
                this.priceError.details.push({
                    'attributeName': 'offeringPriceType',
                    'constraintMessage': this.$t('offering.error.chooseType'),
                });
            }
            if (this.offeringPricing !== Offering.PRICING_FREE && !this.offeringPricesClubFee) {
                this.priceError.details.push({
                    'attributeName': 'offeringPricesClubFee',
                    'constraintMessage': this.$t('offering.error.chooseClubFee'),
                });
            }
            if (this.offeringPriceType && this.offeringPriceType.value === 'department_price' && !this.offeringPriceDepartment) {
                this.priceError.details.push({
                    'attributeName': 'offeringPriceDepartment',
                    'constraintMessage': this.$t('offering.error.chooseDepartement'),
                });
            }
            if (this.offeringPriceType && this.offeringPriceType.value === 'team_price' && !this.offeringPriceTeam) {
                this.priceError.details.push({
                    'attributeName': 'offeringPriceTeam',
                    'constraintMessage': this.$t('offering.error.chooseTeam'),
                });
            }

            if (this.priceError.details.length !== 0) {
                return;
            }

            if (this.offeringPrices && this.currentlyEditedOfferingPriceIndex !== null) {
                this.offeringPrices.splice(this.currentlyEditedOfferingPriceIndex, 1);
            }

            this.offeringPrices.push({
                'clubFee': {'id': (this.offeringPricesClubFee ? this.offeringPricesClubFee.value : null)},
                'department': (this.offeringPriceDepartment ? this.offeringPriceDepartment.value : null),
                'team': (this.offeringPriceTeam ? this.offeringPriceTeam.value : null),
                'divergentAmount': this.offeringPrice && this.offeringPrice !== '' ? {
                    'numerator': parseInt(parseFloat(this.offeringPrice.replace(',', '.')) * 100),
                    'denominator': 100
                } : null,
                'type': (this.offeringPriceType ? this.offeringPriceType.value : null),
            });
            this.offeringPricesId = null;
            this.offeringPricesClubFee = null;
            this.offeringPriceDepartment = null;
            this.offeringPriceTeam = null;
            this.offeringPriceType = null;
            this.offeringPrice = null;
            this.currentlyEditedOfferingPriceIndex = null;
            this.offeringPricing = Offering.PRICING_FREE;
            this.showEditPrice = false;
        },
        editPrice: function (priceData, index) {
            this.showEditPrice = true;
            if (parseFloat(priceData.divergentAmount) > 0 || priceData.clubFee) {
                this.offeringPricing = Offering.PRICING_CHARGEBLE;
            } else {
                this.offeringPricing = Offering.PRICING_FREE;
            }
            this.currentlyEditedOfferingPriceIndex = index;
            this.offeringPricesId = priceData.id;
            this.offeringPricesClubFee = priceData.clubFee;
            this.offeringPriceDepartment = priceData.department;
            this.offeringPriceTeam = priceData.team;
            this.offeringPriceType = priceData.type;
            this.offeringPrice = this.formatWithoutCurrency(priceData.divergentAmount);
        },
        removePrice: function (index) {
            this.offeringPrices.splice(index, 1);
        },
        onKeyUpEnter() {
            this.$emit('enter');
        },
        changeInSchoolHolidays() {
            this.offering.takesPlaceInSchoolHolidays = this.takesPlaceInSchoolHolidays;
        },
        changeOnPublicHolidays() {
            this.offering.takesPlaceOnPublicHoliday = this.takesPlaceOnPublicHoliday;
        },
        changeExecutionType() {
            if (this.executionType.value === null || this.executionType.value === 'presence') {
                this.offering.onlineUrl = '';
            }
            this.offering.executionType = this.executionType;
        },
        changeShowAvailablePlaces() {
            this.offering.showAvailablePlaces = this.showAvailablePlaces;
        },
        changeSelectableExams() {
            this.offering.selectableExams = this.selectableExams;
        },
        changeRegistrationRequested() {
            this.offering.registrationRequested = this.registrationRequested;
        },
        changeStatus() {
            this.offering.status = (this.inactive) ? Offering.STATUS_DRAFT : Offering.STATUS_PUBLISHED;
        },
        changeAccessibleForTeamMembersOnly() {
            this.offering.accessibleForTeamMembersOnly = this.accessibleForTeamMembersOnly;
        },
        getExecutionType(executionType) {
            return this.executionTypes.find(type => type.value === executionType);
        },
        getWeekday(weekdayValue) {
            return this.weekdays.find(weekday => weekday.value === weekdayValue);
        },
        getDepartment(departmentValue) {
            return this.departmentlist.find(department => department.value === departmentValue);
        },
        getInstructors() {
            return Instructors.findAllByClub(this.clubId)
                .then(instructors => {
                    const result = [];
                    instructors.sort((a, b) => (a.fullName.toLowerCase() < b.fullName.toLowerCase()) ? -1 : (a.fullName.toLowerCase() > b.fullName.toLowerCase()) ? 1 : 0);
                    instructors.forEach(instructor => {
                        result.push({
                            'value': instructor.id,
                            'label': instructor.fullName
                        });
                    })
                    return result;
                });
        },
        getLocations() {
            return LocationRepository.findAllByClubId(this.clubId)
                .then(locations => {
                    const result = [];
                    locations.forEach(location => {
                        if (!location.isArchived) {
                            result.push({
                                'name': location.name,
                                'value': location.id
                            });
                        }
                    });
                    this.locationsFullArray = locations;
                    return result;
                })
                .catch(() => this.$notify.error('error.loadingData'));
        },
        getLocationAreas() {
            this.areas = [];
            this.areaFullArray = [];
            if (this.locationsFullArray) {
                this.locationsFullArray.forEach(location => {
                    if (this.selectedLocation !== null && location.id === this.selectedLocation.value) {
                        location.areas.forEach(area => {
                            if (!area.isArchived) {
                                this.areas.push({
                                    'name': area.hierarchyPath + ' / ' + area.name,
                                    'value': area.id
                                });
                            }
                            this.areaFullArray.push(area);
                        })
                    }
                });
            }
        },
        getMemberClubFees() {
            const priceFormatter = new PriceFormatter();
            return OfferingClubFeeRepository.findByClubId(this.clubId)
                .then(clubFees => {
                    const result = [];
                    clubFees.forEach(clubFee => {
                        result.push({
                            'name': clubFee.name + ' (' + priceFormatter.format(clubFee.priceHistory.getCurrentPricePerTime(), true) + ')',
                            'value': clubFee.id
                        });
                    });
                    return result;
                })
                .catch(() => this.$notify.error('error.loadingData'));
        },
        getDepartments() {
            return OfferingDepartmentRepository.findAll(this.clubId)
                .then(departments => {
                    const result = [];
                    departments.sort((a, b) => (a.departmentName + ' / ' + a.name).toLowerCase().localeCompare((b.departmentName + ' / ' + b.name).toLowerCase()));
                    departments.forEach(department => {
                        result.push({
                            'label': department.name,
                            'value': department.id
                        });
                        this.getChildDepartments(department);
                    })
                    return result;
                })
                .catch(() => this.$notify.error('error.loadingData'));
        },
        getChildDepartments(department) {
            department.childDepartments.sort((a, b) => ((a.name).toLowerCase() < (b.name).toLowerCase()) ? -1 : ((a.name).toLowerCase() > (b.name).toLowerCase()) ? 1 : 0);
            department.childDepartments.forEach(childDepartment => {
                this.departmentlist.push({
                    'label': childDepartment.hierarchyPath + ' / ' + childDepartment.name,
                    'value': childDepartment.id
                });
                this.getChildDepartments(childDepartment);
            });
        },
        getTeams() {
            return OfferingTeamRepository.findAll(this.clubId)
                .then(teams => {
                    const result = [];
                    teams.sort((a, b) => ((a.departmentName + ' / ' + a.name).toLowerCase() < (b.departmentName + ' / ' + b.name).toLowerCase()) ? -1 : ((a.departmentName + ' / ' + a.name).toLowerCase() > (b.departmentName + ' / ' + b.name).toLowerCase()) ? 1 : 0);
                    teams.forEach(team => {
                        result.push({
                            'label': team.departmentName + ' / ' + team.name,
                            'value': team.id
                        });
                    })
                    return result;
                })
                .catch(() => this.$notify.error('error.loadingData'));
        },
        getAreaFields() {
            this.fieldList = [];
            if (this.areaFullArray) {
                this.areaFullArray.forEach(area => {
                    if (this.selectedArea !== null && area.id === this.selectedArea.value) {
                        area.fields.forEach(field => {
                            if (!field.isArchived) {
                                this.fieldList.push({
                                    'label': field.hierarchyPath + ' / ' + field.name,
                                    'value': field.id
                                });
                            }
                        })
                    }
                });
            }
        },
        setBookingStartDate() {
            if (this.bookingStart) {
                this.offering.bookingStart = moment(this.bookingStart, 'DD.MM.YYYY').format('YYYY-MM-DD');
            } else {
                this.offering.bookingStart = null;
            }
        },
        setBookingEndDate() {
            if (this.bookingEnd) {
                this.offering.bookingEnd = moment(this.bookingEnd, 'DD.MM.YYYY').format('YYYY-MM-DD');
            } else {
                this.offering.bookingEnd = null;
            }
        },
        getBookingdates() {
            if (this.offering.bookingStart) {
                this.bookingStart = moment(this.offering.bookingStart, 'YYYY-MM-DD').format('DD.MM.YYYY');
            }
            if (this.offering.bookingEnd) {
                this.bookingEnd = moment(this.offering.bookingEnd, 'YYYY-MM-DD').format('DD.MM.YYYY');
            }
            if (this.offering.cancellableUntilDate) {
                this.cancellableUntilDate = moment(this.offering.cancellableUntilDate, 'YYYY-MM-DD').format('DD.MM.YYYY');
            }
        },
        setBookingPerAppointmentType() {
            if (this.offeringBookingStartPerAppointmentType) {
                this.offering.bookingStartPerAppointmentType = this.offeringBookingStartPerAppointmentType.value;
            }
            if (this.offeringBookingEndPerAppointmentType) {
                this.offering.bookingEndPerAppointmentType = this.offeringBookingEndPerAppointmentType.value;
            }
        },
        setCancellableUntilType() {
            this.offering.cancellableUntilType = this.cancellableUntilType ? this.cancellableUntilType.value : null;
        },
        getBookingPerAppointmentType() {
            if (this.offering.bookingStartPerAppointmentType) {
                this.offeringBookingStartPerAppointmentType = this.bookingPerAppointmentTypes.find(type => type.value === this.offering.bookingStartPerAppointmentType);
            }
            if (this.offering.bookingEndPerAppointmentType) {
                this.offeringBookingEndPerAppointmentType = this.bookingPerAppointmentTypes.find(type => type.value === this.offering.bookingEndPerAppointmentType);
            }
            if (this.offering.cancellableUntilType) {
                this.cancellableUntilType = this.bookingPerAppointmentTypes.find(type => type.value === this.offering.cancellableUntilType);
            }
        },
        setCancellableUntilDate() {
            if (this.cancellableUntilDate) {
                this.offering.cancellableUntilDate = moment(this.cancellableUntilDate, 'DD.MM.YYYY').format('YYYY-MM-DD');
            } else {
                this.offering.cancellableUntilDate = null;
            }
        },
        getBookingType() {
            if (this.offering.bookingType) {
                this.offeringBookingType = this.offeringBookingTypes['offeringBookingType.' + this.offering.bookingType];
            } else {
                this.offeringBookingType = this.offeringBookingTypes['offeringBookingType.perOffering'];
            }
        },
        switchOfferingBookingType(type) {
            this.offering.bookingType = type;
            if (type === Offering.BOOKING_TYPE_PER_OFFERING) {
                this.offering.bookingStartPerAppointmentAmount = null;
                this.offering.bookingStartPerAppointmentType = null;
                this.offeringBookingStartPerAppointmentType = null;
                this.offering.bookingEndPerAppointmentAmount = null;
                this.offering.bookingEndPerAppointmentType = null;
                this.offeringBookingEndPerAppointmentType = null;
                this.offering.cancellableUntilAmount = null;
                this.cancellableUntilType = null;
                this.offering.cancellableUntilType = null;
            } else {
                this.offering.bookingStart = null;
                this.offering.bookingEnd = null;
                this.bookingStart = null;
                this.bookingEnd = null;
                this.offering.cancellableUntilDate = null;
                this.cancellableUntilDate = null;
            }
        },
        getPrices() {
            const result = [];
            if (this.offering.prices) {
                this.offering.prices.forEach(
                    price => {
                        result.push({
                            'clubFee': price.clubFee,
                            'department': price.department,
                            'id': price.id,
                            'team': price.team,
                            'divergentAmount': price.divergentAmount,
                            'type': price.type,
                        });
                    }
                );
            }
            return result;
        },
        getInstructorsSelect() {
            const result = [];
            if (this.offering.instructors) {
                this.offering.instructors.forEach(
                    instructor => result.push({
                        'label': instructor.fullName,
                        'value': instructor.id
                    })
                );
            }
            return result;
        },
        getFieldSelect() {
            const result = [];
            if (this.offering.fields) {
                this.offering.fields.forEach(
                    field => result.push({
                        'label': field.location.name + ' / ' + field.area.name + ' / ' + field.name,
                        'value': field.id
                    })
                );
            }
            return result;
        },
        formatWithoutCurrency(amount) {
            if (amount) {
                return new Helper().numberFormat(amount, this.locale);
            } else {
                return null;
            }
        },
        updateAppointmentSeries(newAppointmentSeries) {
            this.offering.appointmentSeriesList = newAppointmentSeries;
        },
    },
}
</script>

<style scoped lang="scss" rel="stylesheet/scss">
</style>
